import { safePromise } from "../../../Helpers/Functions/promise"
import {
    APITaskDetailsModel,
    APITaskListParams,
    APITaskModel
} from "../../../Models/API/TaskModel"
import { hasValue } from "../../../Models/Client/FetchState"
import { MainThunkDispatch, State } from "../../../Reducers"
import { apiEndpoints } from "../../../Routes/api"
import { failMessage } from "../../Client/MessagesActions"
import { apiFetch, killPoll, pollAPI } from "../../Client/NetworkActions"
import { TaskAPIType } from "../../Types"

const DEFAULT_PAGE_SIZE = 1000
const TASK_LIST_POLL = "task_list_poll"
const TASK_DETAILS_POLL = "task_details_poll"

export const getTaskDetails =
    (versionJobId: string, taskID: string) =>
    async (dispatch: MainThunkDispatch) => {
        const jobID = versionJobId
        const { data, error } = await safePromise(
            dispatch(
                apiFetch(
                    `GET_TASK_DETAILS_${versionJobId}_${taskID}`,
                    apiEndpoints.getTaskDetails({ jobID, taskID })
                )
            )
        )

        if (error !== undefined || data === undefined) {
            return
        }

        dispatch({
            type: TaskAPIType.setDetails,
            meta: {
                versionJobId,
                taskID
            },
            payload: data
        })
    }

export const stopTaskListPoll = () => killPoll(TASK_LIST_POLL)

export const startTaskListPoll =
    (
        versionJobId: string,
        params: APITaskListParams,
        page: number = 0,
        pageSize: number = DEFAULT_PAGE_SIZE
    ) =>
    async (dispatch: MainThunkDispatch) => {
        const jobID = versionJobId
        const handleResponse = async (data: APITaskModel | undefined) => {
            if (data === undefined) {
                return
            }

            dispatch({
                type: TaskAPIType.setList,
                meta: {
                    versionJobId,
                    page,
                    total: data.total,
                    params
                },
                payload: data.tasks
            })
        }

        dispatch(
            pollAPI<APITaskModel>(
                TASK_LIST_POLL,
                apiEndpoints.getTaskList({ jobID, page, pageSize, ...params }),
                handleResponse,
                undefined,
                5000,
                true
            )
        )
    }

export const stopTaskDetailsPoll = () => killPoll(TASK_DETAILS_POLL)

export const startTaskDetailsPoll =
    (versionJobId: string, taskID: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const jobID = versionJobId
        const handleResponse = async (
            data: APITaskDetailsModel | undefined
        ) => {
            const taskDetails =
                getState().api.tasks.details?.[versionJobId]?.[taskID]
            const hasTaskChanged = hasValue(taskDetails)
                ? JSON.stringify(taskDetails?.value) !== JSON.stringify(data)
                : true

            if (data === undefined || !hasTaskChanged) {
                return
            }

            dispatch({
                type: TaskAPIType.setDetails,
                meta: {
                    versionJobId,
                    taskID
                },
                payload: data
            })
        }

        dispatch(
            pollAPI<APITaskDetailsModel>(
                TASK_DETAILS_POLL,
                apiEndpoints.getTaskDetails({ jobID, taskID }),
                handleResponse,
                undefined,
                5000,
                true
            )
        )
    }

export const markTaskCorrectness =
    (versionJobId: string, taskID: string, correct: boolean) =>
    async (dispatch: MainThunkDispatch) => {
        const jobID = versionJobId
        const { data, error } = await safePromise(
            dispatch(
                apiFetch(
                    `REVIEW_TASK_${versionJobId}_${taskID}`,
                    apiEndpoints.reviewTask({ jobID, taskID }),
                    {
                        method: "PATCH",
                        body: JSON.stringify({
                            correct
                        })
                    },
                    200
                )
            )
        )

        if (error !== undefined || data === undefined) {
            if (error !== undefined) {
                dispatch(
                    failMessage("Could not update task correctness", error)
                )
            }
            return
        }

        dispatch({
            type: TaskAPIType.setDetails,
            meta: {
                versionJobId,
                taskID
            },
            payload: data
        })
    }
